/**
 * LocationLeaderService
 * Service for LocationLeader entity operations
 * Route: api/location-leaders
 * Controller: LocationLeadersController.cs
 * Note: Uses composite key (LocationID + MemberID)
 */
import { CrudService } from '../base/CrudService';
/**
 * LocationLeaderService
 * Handles location leader CRUD operations and custom endpoints
 * Maps to /api/location-leaders endpoint
 */
export class LocationLeaderService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? LocationLeaderService.DEFAULT_SERVICE_PATH,
            basePath: LocationLeaderService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get all leaders for a specific location
     * GET /api/location-leaders/location/{locationId}
     * @param locationId - Location ID
     * @returns Array of location leaders
     */
    async getByLocationId(locationId) {
        const response = await this.getByLocationIdRaw(locationId);
        return response.data ?? [];
    }
    /**
     * Get all leaders for a specific location (raw)
     * @param locationId - Location ID
     * @returns ResponseData with array of location leaders
     */
    getByLocationIdRaw(locationId) {
        const endpoint = this.getEndpoint('byLocationId', `location/${locationId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get a location leader by composite key (LocationID + MemberID)
     * GET /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns Location leader
     */
    async getByCompositeKey(locationId, memberId) {
        const response = await this.getByCompositeKeyRaw(locationId, memberId);
        if (!response.data) {
            throw new Error(`Location leader (Location: ${locationId}, Member: ${memberId}) not found`);
        }
        return response.data;
    }
    /**
     * Get a location leader by composite key (raw)
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns ResponseData with location leader
     */
    getByCompositeKeyRaw(locationId, memberId) {
        const endpoint = this.getEndpoint('byCompositeKey', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Add a leader to a location
     * POST /api/location-leaders
     * @param request - Add location leader request
     * @returns Created location leader
     */
    async addLeader(request) {
        const response = await this.addLeaderRaw(request);
        if (!response.data) {
            throw new Error('Failed to add location leader');
        }
        return response.data;
    }
    /**
     * Add a leader to a location (raw)
     * @param request - Add location leader request
     * @returns ResponseData with created location leader
     */
    addLeaderRaw(request) {
        const endpoint = this.getEndpoint('create', '');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Update a location leader assignment
     * PUT /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @param locationLeader - Location leader data to update
     * @returns Updated location leader
     */
    async updateByCompositeKey(locationId, memberId, locationLeader) {
        const response = await this.updateByCompositeKeyRaw(locationId, memberId, locationLeader);
        if (!response.data) {
            throw new Error(`Failed to update location leader (Location: ${locationId}, Member: ${memberId})`);
        }
        return response.data;
    }
    /**
     * Update a location leader assignment (raw)
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @param locationLeader - Location leader data to update
     * @returns ResponseData with updated location leader
     */
    updateByCompositeKeyRaw(locationId, memberId, locationLeader) {
        const endpoint = this.getEndpoint('update', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, locationLeader);
    }
    /**
     * Remove a leader from a location
     * DELETE /api/location-leaders/{locationId}/{memberId}
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns True if removed successfully
     */
    async removeLeader(locationId, memberId) {
        const response = await this.removeLeaderRaw(locationId, memberId);
        return response.success;
    }
    /**
     * Remove a leader from a location (raw)
     * @param locationId - Location ID
     * @param memberId - Member ID
     * @returns ResponseData
     */
    removeLeaderRaw(locationId, memberId) {
        const endpoint = this.getEndpoint('delete', `${locationId}/${memberId}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
}
LocationLeaderService.DEFAULT_SERVICE_PATH = 'locations';
LocationLeaderService.BASE_PATH = 'api/location-leaders';
//# sourceMappingURL=LocationLeaderService.js.map